import { useSearch } from "~/context/SearchContext";
import LabelDeletable from "../LabelDeletable";
import type { Masters, Condition } from "~/types";
import { useNavigate, useSearchParams } from "@remix-run/react";

type Props = {
  masters: Masters;
};

const SelectedArea = ({ masters }: Props) => {
  const { appliedConditions: listLoadConditions } = useSearch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const getLabelColor = (key: keyof Condition) => {
    switch (key) {
      case "occupation_type_ids":
        return "primary";
      case "service_type_ids":
        return "secondary";
      case "search_area_ids":
        return "tertiary";
      case "free_word":
        return "default";
      default:
        return "default";
    }
  };

  const getText = (key: keyof Condition, id: string) => {
    switch (key) {
      case "occupation_type_ids":
        return (
          masters.occupationTypes.find((item) => item.id === id)?.name || id
        );
      case "service_type_ids":
        return masters.serviceTypes.find((item) => item.id === id)?.name || id;
      case "search_area_ids":
        return masters.searchAreas.find((item) => item.id === id)?.name || id;
      case "free_word":
        return `「${id}」を含む転職・求人情報`;
      default:
        return id;
    }
  };

  // フリーワード検索のキーワード全体を取得
  const getFreeWordText = () => {
    const freeWord = searchParams.get("free_word");
    if (!freeWord || !freeWord.trim()) return null;

    return freeWord.trim().replace(/　/g, ' ');
  };

  // フリーワード検索全体を削除するハンドラー
  const handleRemoveFreeWord = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("free_word");
    navigate(`?${newParams.toString()}`);
  };

  // フリーワードキーワードを既存の条件と同じ形式で処理
  const allConditions = {
    ...listLoadConditions,
    ...(getFreeWordText() && {
      free_word: [getFreeWordText()]
    })
  };

  const hasNoConditions = Object.keys(allConditions).every(
    (key) => allConditions[key as keyof Condition].length === 0
  );

  return (
    <div className="bg-white rounded-lg shadow-[1px_1px_6px_0_rgba(51,51,51,0.15)]">
      <h4 className="py-2 px-4 rounded-[8px_8px_0_0] bg-gray-light text-sm text-gray leading-[1.4] font-bold md:px-6 md:text-[15px]">
        設定した条件
      </h4>
      {hasNoConditions ? (
        <div className="py-5 px-6 text-gray font-medium text-sm leading-normal md:py-6 md:text-[15px] md:leading-normal">
          <p>検索条件は指定されていません。</p>
        </div>
      ) : (
        <div className="pt-5 pb-1 px-4 pointer-events-none md:pt-6 md:pb-2 md:px-6 [&>div]:mr-4 [&>div]:mb-4">
          {/* すべての条件（職種、業態、エリア、フリーワード）を統一して表示 */}
          {Object.keys(allConditions).map((key) => {
            const labelColor = getLabelColor(key as keyof Condition);

            return allConditions[key as keyof Condition].map((id) => {
              const text = getText(key as keyof Condition, id);

              return (
                <LabelDeletable
                  key={`${key}_${id}`}
                  text={text}
                  labelColor={labelColor}
                  type={key as keyof Condition}
                  value={id}
                  handleSubCondition={
                    key === "free_word" 
                      ? () => handleRemoveFreeWord()
                      : undefined
                  }
                />
              );
            });
          })}
        </div>
      )}
    </div>
  );
};

export default SelectedArea;
